import {
    FC,
    SignalPropsHandler,
    SignalEventsHandler,
    iterChildren,
    CanBeSignals,
    CanBeSignal,
    PointLike,
    copyPointData,
    JSX,
    SpecialProps,
    ExtendProps,
    handleRef
} from '@gongxigames/framework/core/fragments/BuilderY';
import type {Override} from '@gongxigames/framework/lib/types/basic';
import {Container as PixiContainer, DisplayObject } from 'pixi.js';
import {Responsive} from '@gongxigames/framework/core/fragments/display/responsive';
import {ContainerEventProps, PIXI_CONTAINER_EVENTS} from '../PixiEvents';
import {PIXI_CONTAINER_PROPS, PIXI_CONTAINER_SPECIAL_PROPS} from '../PixiProps';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';

type PropsOverrideForContainer = Responsive & {
    position: PointLike;
    scale: PointLike;
    pivot: PointLike;
    events: ContainerEventProps;
};

type ContainerPropsWithoutMethods = Pick<PixiContainer, typeof PIXI_CONTAINER_PROPS[number]>;
type ContainerPropsOverride = Override<ContainerPropsWithoutMethods, PropsOverrideForContainer>;
type ContainerPropsWithoutChildren = Partial<CanBeSignals<ContainerPropsOverride> & SpecialProps<PixiContainer<DisplayObject>>>;

export type ContainerProps = Override<ContainerPropsWithoutChildren, {
    children?: JSX.Element;
    layer?: CanBeSignal<keyof typeof LAYERS>;
    zOrder?: number;
    invisible?: CanBeSignal<boolean>;
    name?: CanBeSignal<string|undefined>;
}>;

const DESTROY_HANDLER = Symbol('DESTROY_HANDLER');

const containerPropsHandler = new SignalPropsHandler<ContainerProps, PixiContainer>()
    .propsToHandle(PIXI_CONTAINER_SPECIAL_PROPS)
    .setHandlerFor('children', (children, obj) => {
        obj.removeChildren();

        for (const child of iterChildren(children)) {
            obj.addChild(child);

            if (!(child as any)[DESTROY_HANDLER]) {
                (child as any)[DESTROY_HANDLER] = true;

                obj.on('destroyed', () => {
                    if (!child.parent && !child.destroyed) {
                        child.destroy({children: true});
                    }
                });
            }
        }

    }, {forceHandleAsSignal: true})
    .setHandlerFor('position', (val, obj) => copyPointData(val, obj.position))
    .setHandlerFor('pivot', (val, obj) => copyPointData(val, obj.pivot))
    .setHandlerFor('scale', (val, obj) => copyPointData(val, obj.scale))
    .setHandlerFor('layer', (val, obj) => val ? obj.parentGroup = LAYERS[val].group : undefined)
    .setHandlerFor('invisible', (val, obj) => obj.visible = !val);

const containerEventHandler = new SignalEventsHandler<ContainerEventProps>()
    .handleEvents(PIXI_CONTAINER_EVENTS);

/**
 * Component function that constructs
 * basic Pixi Container Component
 */
export const $Container: FC<ContainerProps> = (props) => {
    const container = props.instance || new PixiContainer();

    const unsubscribeProps = containerPropsHandler.apply(props, container);

    container.on('destroyed', unsubscribeProps);

    if (props.events) {
        const unsubscribeEvents = containerEventHandler.apply(props.events, container);

        container.on('destroyed', unsubscribeEvents);
    }

    handleRef(container, props.ref);

    return container;
};

// Helper types
export type ExtendContainerPropsSimple<T> = T & ContainerProps;
export type ExtendContainerProps<T, REF = null> = ExtendProps<T, ContainerProps, REF>;